import { useNavigate } from '@tanstack/react-router'
import { useIsAuthorized } from '~/hooks/useAuthorization'
import { useWorkspaceMembers } from '~/hooks/users/useWorkspaceMembers'
import { useUpdateWorkspaceMember } from '~/hooks/users/useUpdateWorkspaceMember'
import { WorkspaceMemberObject } from '~/features/workspaces/types'
import { Member } from '../types/member'
import { useDeleteWorkspaceMember } from '~/hooks'
import {
  Button,
  showConfirmationPrompt,
  showSuccessToast,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@bpinternal/ui-kit-next'
import { DropdownMenu, Badge, MenuItem, Avatar } from '~/elements'
import { EllipsisVertical } from 'lucide-react'
import { User } from '~/features/users/types'
import { useSuspenseQuery } from '~/services'
import { RoleOptions } from '~/features/users/components'

export const MemberRow = ({
  member,
  currentUser,
  workspaceId,
}: {
  member: Member
  currentUser: User
  workspaceId: string
}) => {
  const { email, role, id, userId } = member
  const isAuthorized = useIsAuthorized({ workspaceId, userId: currentUser.id })
  const navigate = useNavigate()

  const { mutate: updateWorkspaceMember } = useUpdateWorkspaceMember()
  const { mutate: deleteWorkspaceMember } = useDeleteWorkspaceMember()
  const { data: members } = useWorkspaceMembers({ workspaceId })
  const { plan } = useSuspenseQuery('workspaces_/$workspaceId_', { workspaceId }).data

  const currentUserMember = members?.find((m) => m.userId === currentUser.id)

  const onMemberRemoved = (removedMember: WorkspaceMemberObject) => {
    if (currentUser.id === removedMember.userId) {
      // If the user removes themself from the workspace, redirect to home.
      void navigate({ to: '/', replace: true })
      return
    }

    showSuccessToast(`${removedMember.email} has been removed from this workspace.`)
  }

  const menu: MenuItem[] = [
    {
      type: 'submenu',
      content: 'Change role',
      disabled: role === 'owner',
      items: [
        {
          type: 'radioGroup',
          value: role,
          items: RoleOptions.map((option) => ({
            type: 'radioItem',
            value: option.role,
            disabled: option.role === role || (option.role !== 'administrator' && plan === 'community'),
            content: (
              <div className="flex flex-col">
                <Tooltip delayDuration={500}>
                  <TooltipTrigger asChild>
                    <span>{option.label}</span>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={16} className="max-w-64">
                    {option.description}
                  </TooltipContent>
                </Tooltip>
              </div>
            ),
            onSelect: () =>
              showConfirmationPrompt(
                <span className="text-sm">
                  Are you sure you want to change the role of <span className="font-bold">{email} </span>
                  from <span className="font-bold">{role} </span>
                  to <span className="font-bold">{option.role} </span>?
                </span>,
                {
                  title: 'Change role',
                }
              ).then(() =>
                updateWorkspaceMember({
                  id,
                  workspaceId,
                  role: option.role,
                  onSuccess: async () => {
                    showSuccessToast(`The role of ${email} has been successfully changed to ${option.role}.`)
                  },
                })
              ),
          })),
        },
      ],
    },
    { type: 'separator' },
    {
      type: 'item',
      content: 'Remove',
      danger: true,
      onSelect: () => {
        void showConfirmationPrompt(
          userId === currentUser.id ? (
            <span className="text-sm">
              You are about to <span className="font-bold">remove yourself</span> from the workspace. You will no longer
              have access to this workspace.
              <br />
              <br /> Are you sure you want to proceed?
            </span>
          ) : (
            <span className="text-sm">
              Are you sure you want to remove <span className="font-bold">{email}</span> from the workspace?
            </span>
          ),
          {
            variant: 'danger',
            title: 'Remove member',
          }
        ).then(() =>
          deleteWorkspaceMember({
            workspaceId,
            id,
            requesterMemberId: currentUserMember?.id ?? '',
            onSuccess: async () => {
              onMemberRemoved(member)
            },
          })
        )
      },
    },
  ]

  return (
    <div className="flex items-center gap-2 p-3">
      <Avatar
        name={member.displayName ?? member.email}
        pictureUrl={member.profilePicture}
        className="h-8 w-8"
        variant="solid"
      />
      <span className="pr-2 text-sm font-medium">{email}</span>
      {currentUser.email === email && (
        <Badge variant="outline" color="blue">
          You
        </Badge>
      )}
      <span className="ml-auto text-sm text-gray-11">{role}</span>
      {isAuthorized('workspace.manageMembers') ? (
        <DropdownMenu content={menu}>
          <Button variant="ghost" color="gray" size="icon">
            <EllipsisVertical />
          </Button>
        </DropdownMenu>
      ) : (
        <div />
      )}
    </div>
  )
}
